/**
 * useClientBudget Hook
 *
 * React hook for fetching budget settings for a specific client and year.
 * Includes loading states, error handling, and in-memory caching.
 *
 * @module hooks/useClientBudget
 * @description Provides client-specific budget data including PROA targets,
 * labour costs, and approved work amounts. Caches data by client-year combination.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { getClientBudget } from '@/api/budgets'
import type { ClientBudgetSettings } from '@/types/budgets'

/**
 * Hook state interface
 */
interface UseClientBudgetResult {
  /** Client budget data or null if not loaded/not found */
  data: ClientBudgetSettings | null
  /** True when fetching data from API */
  loading: boolean
  /** Error object if fetch failed, null otherwise */
  error: Error | null
  /** Function to manually refresh data (bypasses cache) */
  refetch: () => Promise<void>
}

/**
 * Cache structure: "clientCode-year" -> ClientBudgetSettings
 * Example key: "000054-2025"
 */
type ClientBudgetCache = Record<string, ClientBudgetSettings>

/**
 * Generate cache key from client code and year
 *
 * @param clientCode - Client identifier
 * @param year - Budget year
 * @returns Cache key string
 */
function getCacheKey(clientCode: string, year: number): string {
  return `${clientCode}-${year}`
}

/**
 * Fetch and cache budget settings for a specific client and year
 *
 * @param clientCode - Unique client identifier (e.g., '000054')
 * @param year - Budget year (e.g., 2025)
 * @returns Hook state with data, loading, error, and refetch function
 *
 * @example
 * function ClientDashboard({ clientCode }: { clientCode: string }) {
 *   const { data, loading, error, refetch } = useClientBudget(clientCode, 2025)
 *
 *   if (loading) return <div>Loading budget...</div>
 *   if (error) return <div>Error: {error.message}</div>
 *   if (!data) return <div>No budget data for this client</div>
 *
 *   return (
 *     <div>
 *       <h2>{data.clientName} - Budget {data.year}</h2>
 *       <p>Target Revenue: {data.settings.targetRevenue}</p>
 *       <p>Labour Cost: {data.settings.labourCost}</p>
 *       <button onClick={refetch}>Refresh</button>
 *     </div>
 *   )
 * }
 */
export function useClientBudget(clientCode: string, year: number): UseClientBudgetResult {
  // State management
  const [data, setData] = useState<ClientBudgetSettings | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  // In-memory cache (persists across re-renders but not page reloads)
  const cache = useRef<ClientBudgetCache>({})

  /**
   * Fetch client budget data from API or cache
   *
   * @param bypassCache - If true, ignores cache and forces fresh fetch
   */
  const fetchData = useCallback(
    async (bypassCache: boolean = false) => {
      try {
        setLoading(true)
        setError(null)

        const cacheKey = getCacheKey(clientCode, year)

        // Check cache first (unless bypassing)
        if (!bypassCache && cache.current[cacheKey]) {
          setData(cache.current[cacheKey])
          setLoading(false)
          return
        }

        // Fetch from API
        const result = await getClientBudget(clientCode, year)

        // Handle 404 (budget not found) gracefully
        if (result === null) {
          setData(null)
          setError(null) // Not an error, just no data
        } else {
          // Update cache and state
          cache.current[cacheKey] = result
          setData(result)
          setError(null)
        }
      } catch (err) {
        // Handle errors
        const error = err instanceof Error ? err : new Error('Failed to fetch client budget')
        setError(error)
        setData(null)
      } finally {
        setLoading(false)
      }
    },
    [clientCode, year]
  )

  /**
   * Refetch function for manual refresh
   * Bypasses cache and fetches fresh data from API
   */
  const refetch = useCallback(async () => {
    // Clear cache for this client-year before refetching
    const cacheKey = getCacheKey(clientCode, year)
    delete cache.current[cacheKey]
    await fetchData(true)
  }, [clientCode, year, fetchData])

  // Fetch data when clientCode or year changes
  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    data,
    loading,
    error,
    refetch,
  }
}
